import { useMutation, useQueryClient } from '@tanstack/react-query';
import { broadcasts } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Badge, Group, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';
import { startErrorMessage, cancelErrorMessage, isDailyLimit } from './startError';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { statusColor } from './OverviewTab';
import { broadcastName } from '../../components/channel';
import { broadcastFailureText, broadcastStatusLabel } from '../../components/delivery';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

interface Step {
  label: string;
  at: string | null;
  state: 'done' | 'pending' | 'failed' | 'running';
}

export function LifecycleTab({ broadcast }: { broadcast: broadcasts.Broadcast }) {
  const t = useT();
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: (overLimit: boolean = false) => broadcasts.startBroadcast(broadcast.id, overLimit ? { allowOverLimit: true } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['broadcast', broadcast.id] });
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      notifySuccess(t('broadcasts.view.lifecycle.toast.started'), t('toast.saved'));
    },
    onError: (err) => {
      // More recipients than the number may reach today (2 October 2026):
      // say so, and offer to send as far as the limit allows.
      if (isDailyLimit(err)) {
        openConfirmModal({
          title: t('broadcasts.start.dailyLimitTitle'),
          children: startErrorMessage(t, err),
          labels: { confirm: t('broadcasts.start.overLimit'), cancel: t('action.cancel') },
          onConfirm: () => start.mutate(true)
        });
        return;
      }
      notifyError(startErrorMessage(t, err), t('toast.saveFailed'));
    }
  });
  const cancel = useMutation({
    mutationFn: () => broadcasts.cancelBroadcast(broadcast.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['broadcast', broadcast.id] });
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      notifySuccess(t('broadcasts.view.lifecycle.toast.cancelled'), t('toast.saved'));
    },
    onError: (err) => notifyError(cancelErrorMessage(t, err), t('toast.saveFailed'))
  });

  const isDraft = /^(draft|pending|scheduled)/i.test(broadcast.status_code);
  const isFinished = !!broadcast.finished_at;
  const failure = broadcastFailureText(t, broadcast);

  const steps: Step[] = [
    { label: t('broadcasts.view.lifecycle.createdDraft'), at: broadcast.created_at, state: 'done' },
    {
      label: t('status.scheduled'),
      at: broadcast.scheduled_at ?? null,
      state: broadcast.scheduled_at ? 'done' : 'pending'
    },
    {
      label: t('broadcasts.view.lifecycle.started'),
      at: broadcast.started_at ?? null,
      state: broadcast.started_at ? (isFinished ? 'done' : 'running') : 'pending'
    },
    {
      label: t('broadcasts.view.lifecycle.finished', { status: broadcastStatusLabel(t, broadcast.status_code) }),
      at: broadcast.finished_at ?? null,
      state: isFinished
        ? /fail|error|cancel/i.test(broadcast.status_code)
          ? 'failed'
          : 'done'
        : 'pending'
    }
  ];

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Title order={4}>{t('broadcasts.view.tabs.lifecycle')}</Title>
            <Badge size="xs" variant="light" color={statusColor(broadcast.status_code)}>
              {broadcastStatusLabel(t, broadcast.status_code)}
            </Badge>
          </Group>
          <Stack gap={0}>
            {steps.map((step, i) => (
              <Group
                key={i}
                gap="md"
                align="flex-start"
                wrap="nowrap"
                style={{
                  padding: '12px 0',
                  borderBottom:
                    i < steps.length - 1 ? '1px dashed var(--rr-color-border, #1f2937)' : 'none'
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    marginTop: 6,
                    background:
                      step.state === 'failed'
                        ? 'var(--rr-color-danger)'
                        : step.state === 'done'
                          ? 'var(--rr-color-success, #10b981)'
                          : step.state === 'running'
                            ? 'var(--rr-color-info, #3b82f6)'
                            : 'var(--rr-color-text-muted, #94a3b8)'
                  }}
                />
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="sm" align="baseline">
                    <Text fz="sm">{step.label}</Text>
                    {step.state === 'running' && (
                      <Badge size="xs" variant="dot" color="blue">{t('broadcasts.view.lifecycle.live')}</Badge>
                    )}
                    {step.state === 'pending' && (
                      <Badge size="xs" variant="outline" color="gray">{t('broadcasts.view.lifecycle.pending')}</Badge>
                    )}
                  </Group>
                  <Text fz="xs" c={MUTED}>
                    {step.at ? `${formatDateTime(step.at)} (${formatRelative(step.at)})` : t('broadcasts.view.overview.notYet')}
                  </Text>
                  {step.state === 'failed' && failure && <Text fz="xs" c="var(--rr-color-error, #dc2626)">{failure}</Text>}
                </Stack>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('common.actions')}</Title>
          {isDraft && (
            <Alert color="blue" variant="light">
              {t('broadcasts.view.lifecycle.draftHint')}
            </Alert>
          )}
          <Group gap="xs">
            <Button
              size="xs"
              variant="filled"
              color="green"
              loading={start.isPending}
              disabled={!isDraft}
              // §4 item 7: this fired straight from the click, with no
              // confirmation and no recipient count at the point of no return.
              onClick={() => openConfirmModal({
                title: t('broadcasts.modal.start.title'),
                children: (
                  <Stack gap={8}>
                    <Text fz="sm">
                      {t('broadcasts.modal.start.body', {
                        count: broadcast.recipients_total,
                        template: broadcastName(t, broadcast)
                      })}
                    </Text>
                    <Text fz="sm" c={MUTED}>{t('broadcasts.modal.start.irreversible')}</Text>
                  </Stack>
                ),
                labels: { confirm: t('action.start'), cancel: t('action.cancel') },
                onConfirm: () => start.mutate(false)
              })}
            >
              {t('broadcasts.view.lifecycle.startNow')}
            </Button>
            <Button
              size="xs"
              variant="outline"
              color="red"
              loading={cancel.isPending}
              disabled={isFinished}
              onClick={() =>
                openConfirmModal({
                  title: t('broadcasts.view.lifecycle.modal.cancel.title'),
                  children: (
                    <Text fz="sm">
                      {t('broadcasts.view.lifecycle.modal.cancel.body')}
                    </Text>
                  ),
                  labels: { confirm: t('broadcasts.view.lifecycle.cancelBroadcast'), cancel: t('broadcasts.view.lifecycle.modal.cancel.keep') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => cancel.mutate()
                })
              }
            >
              {t('broadcasts.view.lifecycle.cancelBroadcast')}
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
